import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Package, Truck, MapPin, MapPinned, Timer, CircleCheck, PackageSearch, Search, Phone } from "lucide-react";
import { API } from "../config";

const TrackingPage = () => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;
    setLoading(true); setSearched(true);
    try { const response = await axios.get(`${API}/shipments/track/${trackingNumber}`); setShipment(response.data); }
    catch (error) { toast.error("No se encontró el envío."); setShipment(null); }
    finally { setLoading(false); }
  };

  const statusConfig = {
    recibido: { color: "bg-blue-500", label: "Recibido", icon: Package },
    en_proceso: { color: "bg-yellow-500", label: "En Proceso", icon: Timer },
    en_transito: { color: "bg-purple-500", label: "En Tránsito", icon: Truck },
    en_camino: { color: "bg-orange-500", label: "En Camino", icon: MapPinned },
    entregado: { color: "bg-green-500", label: "Entregado", icon: CircleCheck },
  };

  const getStatusIndex = (status) => ["recibido", "en_proceso", "en_transito", "en_camino", "entregado"].indexOf(status);

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="tracking-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="font-mono text-xs font-bold text-[#F57C00] tracking-[0.2em] uppercase">SEGUIMIENTO EN TIEMPO REAL</span>
          <h1 className="text-3xl md:text-4xl font-black text-[#5D4037] mt-4">Rastrea tu envío</h1>
          <p className="text-stone-600 mt-4">Ingresa tu número de guía para conocer el estado de tu paquete</p>
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl p-8 shadow-lg border border-stone-100">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <PackageSearch size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
              <input type="text" data-testid="tracking-input" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())} placeholder="Ej: ZNG1234567890" className="w-full h-14 pl-12 pr-4 rounded-xl border border-stone-200 focus:border-[#2E7D32] outline-none text-lg font-mono" />
            </div>
            <button type="submit" data-testid="tracking-search-btn" disabled={loading} className="btn-primary h-14 px-8 flex items-center justify-center gap-2">
              {loading ? "Buscando..." : <><Search size={20} /> Rastrear</>}
            </button>
          </form>
        </motion.div>
        <AnimatePresence>
          {searched && shipment && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-8">
              <div className="bg-white rounded-3xl p-8 shadow-lg border border-stone-100 mb-6" data-testid="tracking-result">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 pb-6 border-b border-stone-100">
                  <div><p className="text-sm text-stone-500">Número de guía</p><p className="text-2xl font-mono font-bold text-[#5D4037]">{shipment.tracking_number}</p></div>
                  <div className={`px-4 py-2 rounded-full ${statusConfig[shipment.status]?.color || "bg-gray-500"} text-white font-semibold flex items-center gap-2`}>
                    {(() => { const Icon = statusConfig[shipment.status]?.icon; return Icon ? <Icon size={18} /> : null; })()}
                    {statusConfig[shipment.status]?.label || shipment.status}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div><p className="text-sm text-stone-500 mb-1">Origen</p><p className="font-semibold text-[#5D4037] flex items-center gap-2"><MapPin size={16} className="text-[#2E7D32]" />{shipment.origin}</p></div>
                  <div><p className="text-sm text-stone-500 mb-1">Destino</p><p className="font-semibold text-[#5D4037] flex items-center gap-2"><MapPinned size={16} className="text-[#F57C00]" />{shipment.destination}</p></div>
                  <div><p className="text-sm text-stone-500 mb-1">Ubicación actual</p><p className="font-semibold text-[#5D4037]">{shipment.current_location}</p></div>
                  <div><p className="text-sm text-stone-500 mb-1">Tiempo estimado</p><p className="font-semibold text-[#5D4037]">{shipment.estimated_delivery}</p></div>
                </div>
              </div>
              <div className="bg-white rounded-3xl p-8 shadow-lg border border-stone-100">
                <h3 className="text-lg font-bold text-[#5D4037] mb-6">Historial del envío</h3>
                <div className="mb-8">
                  <div className="flex justify-between mb-2">
                    {["recibido", "en_proceso", "en_transito", "en_camino", "entregado"].map((status, i) => {
                      const isActive = getStatusIndex(shipment.status) >= i;
                      const Icon = statusConfig[status].icon;
                      return (<div key={status} className="flex flex-col items-center"><div className={`w-10 h-10 rounded-full flex items-center justify-center ${isActive ? statusConfig[status].color : "bg-stone-200"} text-white`}><Icon size={18} /></div><span className={`text-[10px] mt-2 text-center ${isActive ? "text-stone-700 font-medium" : "text-stone-400"}`}>{statusConfig[status].label}</span></div>);
                    })}
                  </div>
                  <div className="h-2 bg-stone-200 rounded-full overflow-hidden mt-4">
                    <div className="h-full bg-gradient-to-r from-[#2E7D32] to-[#4CAF50] transition-all duration-500" style={{ width: `${((getStatusIndex(shipment.status) + 1) / 5) * 100}%` }} />
                  </div>
                </div>
                <div className="space-y-4">
                  {shipment.history?.slice().reverse().map((event, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <div className={`w-3 h-3 rounded-full mt-1.5 ${i === 0 ? "bg-[#4CAF50]" : "bg-stone-300"}`} />
                      <div className="flex-1"><p className="font-semibold text-[#5D4037]">{event.description}</p><p className="text-sm text-stone-500">{event.location}</p><p className="text-xs text-stone-400 mt-1">{new Date(event.timestamp).toLocaleString("es-MX")}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="mt-12 text-center">
          <Link to="/contacto" className="btn-secondary inline-flex items-center gap-2" data-testid="tracking-contact-btn"><Phone size={18} /> Contactar Soporte</Link>
        </div>
      </div>
    </div>
  );
};

export default TrackingPage;
